import { useState, useMemo } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Select, SelectChangeEvent } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { textButtonStyle } from '@/theme';
import { LogsTemplateField } from '@/__generated__/graphql';

const NEW_FIELD_OPTION = '__new_field__';

type FieldSelectorProps = {
  availableFields: LogsTemplateField[];
  onSelectExisting: (fieldKey: string) => void;
  onCreateNew: (fieldName: string, fieldDescription?: string) => void;
  validateNewFieldName?: (name: string) => string;
};

export const FieldSelector = ({
  availableFields,
  onSelectExisting,
  onCreateNew,
  validateNewFieldName,
}: FieldSelectorProps) => {
  const [selected, setSelected] = useState('');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [fieldNameError, setFieldNameError] = useState('');

  const validFields = useMemo(
    () => availableFields.filter((field) => field.key !== null && field.key !== undefined),
    [availableFields]
  );

  const resetForm = () => {
    setSelected('');
    setNewFieldName('');
    setNewFieldDescription('');
    setFieldNameError('');
  };

  const validateFieldName = (name: string): string => {
    if (validateNewFieldName) {
      return validateNewFieldName(name);
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      return 'Field name is required';
    }
    if (trimmedName.length < 2) {
      return 'Field name must be at least 2 characters';
    }
    if (validFields.some((field) => field.key?.toLowerCase() === trimmedName.toLowerCase())) {
      return 'A field with this name already exists';
    }
    return '';
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelected(value);

    if (fieldNameError) {
      setFieldNameError('');
    }

    if (value && value !== NEW_FIELD_OPTION) {
      onSelectExisting(value);
      resetForm();
    }
  };

  const handleFieldNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewFieldName(value);

    if (fieldNameError) {
      setFieldNameError('');
    }
  };

  const handleCreateNew = () => {
    const trimmedName = newFieldName.trim();
    const error = validateFieldName(trimmedName);

    if (error) {
      setFieldNameError(error);
      return;
    }

    onCreateNew(trimmedName, newFieldDescription.trim() || undefined);
    resetForm();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCreateNew();
    }
  };

  const isShowingNewFieldForm = selected === NEW_FIELD_OPTION;
  const isAddButtonDisabled = !newFieldName.trim() || Boolean(fieldNameError);

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        Add field
      </Typography>

      <Select
        size="small"
        value={selected}
        displayEmpty
        onChange={handleSelectChange}
        aria-label="Select field"
      >
        <MenuItem value="" disabled>
          Select field
        </MenuItem>

        {validFields.map((field) => (
          <MenuItem key={field.key} value={field.key || ''}>
            {field.name}
          </MenuItem>
        ))}

        <MenuItem value={NEW_FIELD_OPTION}>
          <Button
            startIcon={<AddCircleIcon />}
            variant="text"
            sx={textButtonStyle}
            fullWidth
            style={{ justifyContent: 'flex-start' }}
          >
            Add New Field
          </Button>
        </MenuItem>
      </Select>

      {isShowingNewFieldForm && (
        <Stack spacing={1}>
          <TextField
            size="small"
            label="New field name"
            value={newFieldName}
            onChange={handleFieldNameChange}
            onKeyDown={handleKeyPress}
            error={Boolean(fieldNameError)}
            helperText={fieldNameError}
            autoFocus
            fullWidth
            placeholder="Enter field name"
          />
          <TextField
            size="small"
            label="Field description (optional)"
            value={newFieldDescription}
            onChange={(e) => setNewFieldDescription(e.target.value)}
            onKeyDown={handleKeyPress}
            fullWidth
            placeholder="Enter field description"
            multiline
            rows={2}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleCreateNew}
              disabled={isAddButtonDisabled}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
